/** /my permissions behind Integration data & import jobs: the page itself, then one Organization action per tab. */
export const INTEGRATION_DATA_PAGE_URL = 'integration-data';
export const INTEGRATION_DATA_PERMISSION_MODULE = 'Organization';
export const IntegrationDataAction = {
  Restlets: 'IntegrationDataAndRestlets',
  ImportJobs: 'ImportJobs',
} as const;

/**
 * The backend `NetSuiteURLType` values offered in "Select Restlet", in enum order. The API sends the enum as its
 * name. ConsumerKey, ConsumerSecret, TokenKey, TokenSecret, Realm, BaseUrl and the QuickBooks* values are
 * credentials, not RESTlets, so they are left out.
 */
export const RESTLET_TYPES = [
  'CreateBill',
  'CreatePayment',
  'CreateVendor',
  'UpdateVendor',
  'DeleteVendor',
  'GetVendors',
  'GetVendor',
  'UpdatePayment',
  'CreateStore',
  'UpdateStore',
  'DeleteStore',
  'GetStores',
  'GetStore',
  'CreateSubsidiary',
  'UpdateSubsidiary',
  'DeleteSubsidiary',
  'GetSubsidiary',
  'GetSubsidiaries',
  'CreateBulkPayment',
  'CreateJournalEntry',
  'UpdateBill',
  'GetChartOfAccounts',
  'DeleteJournalEntry',
  'GetVendorBills',
  'GetPeriod',
  'UpdateJournalEntry',
] as const;

export type RestletType = (typeof RESTLET_TYPES)[number];

/** "CreateBill" → "Create Bill" */
export function restletLabel(type: string): string {
  return (type ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** OrganizationRestletModel */
export interface RestletConfig {
  id: string;
  type: RestletType;
  url: string | null;
}

/** OrganizationRestletRequestModel: `url` is the full RESTlet URL or its path. */
export interface RestletRequest {
  type: RestletType;
  url: string;
}

/** Backend `ImportJobType`, in enum order (an SSE event may carry the enum as its number). */
export const IMPORT_JOB_TYPES = [
  'ImportSubsidiary',
  'ImportVendor',
  'ImportStore',
  'ImportChartOfAccount',
  'ConnectStoreGroup',
  'MappedStoreGroupSubsidiary',
  'MappedChartOfAccount',
  'ImportBillsAndPayment',
  'ImportSecurityDepositChartOfAccount',
] as const;

export type ImportJobType = (typeof IMPORT_JOB_TYPES)[number];

/** Backend `ImportJobStatus`; member order matches the enum's numbers. */
export enum ImportJobStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

/** ImportJobModel */
export interface ImportJob {
  jobType: ImportJobType;
  order: number;
  name: string | null;
  status: ImportJobStatus;
  startedOn: string | null;
  completedOn: string | null;
  /** Seconds the job has been running, shown while in progress. */
  elapsedSeconds: number | null;
  error: string | null;
}

/** An import job update from the app's SSE stream (`{ type: "importJob", companyId, jobType, status, ... }`). */
export interface ImportJobRealtimeEvent {
  companyId: string | null;
  job: ImportJob;
}

const IMPORT_JOB_STATUSES = Object.values(ImportJobStatus);

/** Reads a key in camelCase or PascalCase. */
function readKey(raw: Record<string, unknown>, key: string): any {
  return raw[key] ?? raw[key.charAt(0).toUpperCase() + key.slice(1)];
}

export function isImportJobRealtimeEvent(raw: unknown): boolean {
  return (
    !!raw &&
    typeof raw === 'object' &&
    String(readKey(raw as Record<string, unknown>, 'type') ?? '').toLowerCase() === 'importjob'
  );
}

/** SSE payload → import job update, or null when it isn't one. Enums may come as names or numbers. */
export function toImportJobRealtimeEvent(raw: unknown): ImportJobRealtimeEvent | null {
  if (!isImportJobRealtimeEvent(raw)) return null;
  const get = (key: string) => readKey(raw as Record<string, unknown>, key);

  const jobType = typeof get('jobType') === 'number' ? IMPORT_JOB_TYPES[get('jobType')] : get('jobType');
  const status = typeof get('status') === 'number' ? IMPORT_JOB_STATUSES[get('status')] : get('status');
  if (!jobType || !status) return null;

  return {
    companyId: get('companyId') ?? null,
    job: {
      jobType,
      status,
      order: get('order') ?? 0,
      name: get('name') ?? null,
      startedOn: get('startedOn') ?? null,
      completedOn: get('completedOn') ?? null,
      elapsedSeconds: get('elapsedSeconds') ?? null,
      error: get('error') ?? null,
    },
  };
}
